import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, X, UserPlus, FileText, Image as ImageIcon, Layers3, Presentation,
  Send, Check, MessageSquare, Loader2, ClipboardList, ChevronRight, RefreshCcw, Building2,
} from "lucide-react";
import { uploadOne, fileUrl } from "@/lib/uploads";

const inr = (n) => Number(n || 0).toLocaleString("en-IN");

const STATUS_META = {
  not_submitted: { label: "Not Submitted", tone: "text-gray-400 border-gray-600/40" },
  pending:       { label: "In Review",    tone: "text-amber-300 border-amber-500/40" },
  approved:      { label: "Approved",      tone: "text-emerald-300 border-emerald-500/40" },
  rejected:      { label: "Rejected",      tone: "text-red-300 border-red-500/40" },
};

function StatusPill({ status, testid }) {
  const meta = STATUS_META[status] || STATUS_META.not_submitted;
  return (
    <span data-testid={testid}
          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

export default function OpsOnboardPage() {
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const canSubmit = isAdmin || ["Operations Manager", "Operations Executive"].includes(roleName);
  const canOnboard = canSubmit || roleType === "director" || roleName === "Director";

  const [tab, setTab] = useState("onboard"); // onboard | review
  const [onboarded, setOnboarded] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [openReviewId, setOpenReviewId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [oResp, rResp, revResp] = await Promise.all([
        api.get("/ops/onboard"),
        api.get("/ops/design-reviews"),
        api.get("/ops/onboard/reviewers").catch(() => ({ data: [] })),
      ]);
      setOnboarded(oResp.data);
      setReviews(rResp.data);
      setReviewers(revResp.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setSelectedDetail(null); return; }
    try {
      const { data } = await api.get(`/ops/onboard/${id}`);
      setSelectedDetail(data);
    } catch { setSelectedDetail(null); }
  }, []);
  useEffect(() => { loadDetail(selectedProjectId); }, [selectedProjectId, loadDetail]);

  const openReview = useMemo(() => reviews.find((r) => r.id === openReviewId) || null, [reviews, openReviewId]);

  return (
    <div className="space-y-4" data-testid="ops-onboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Operations · Onboarding</p>
          <h1 className="font-display text-3xl text-white mt-1">Client & Design Approval</h1>
          <p className="text-xs text-gray-500 mt-1">
            Onboard a client, upload design files, submit for a Director / Ops review.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load}
                  className="iz-btn-secondary text-xs flex items-center gap-1.5" data-testid="ops-onboard-refresh">
            <RefreshCcw className="w-3.5 h-3.5"/> Refresh
          </button>
          {canOnboard && (
            <button onClick={() => setShowCreate(true)}
                    className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="ops-onboard-create-btn">
              <Plus className="w-3.5 h-3.5"/> Onboard Client
            </button>
          )}
        </div>
      </div>

      <div className="flex border-b border-[#262626]">
        <TabBtn active={tab === "onboard"} onClick={() => setTab("onboard")} testid="tab-onboard"
                label={`Onboarded (${onboarded.length})`}/>
        <TabBtn active={tab === "review"} onClick={() => setTab("review")} testid="tab-review"
                label={`Quotation & Design Review (${reviews.length})`}/>
      </div>

      {tab === "onboard" && (
        <OnboardTab
          onboarded={onboarded}
          onOpen={(p) => setSelectedProjectId(p.id)}
        />
      )}

      {tab === "review" && (
        <ReviewTab
          reviews={reviews}
          principal={principal}
          isAdmin={isAdmin}
          onOpen={(r) => setOpenReviewId(r.id)}
        />
      )}

      {showCreate && (
        <CreateOnboardModal
          onClose={() => setShowCreate(false)}
          onCreated={(p) => { setShowCreate(false); load(); setSelectedProjectId(p.id); }}
        />
      )}

      {selectedProjectId && selectedDetail && (
        <ProjectDetailDrawer
          detail={selectedDetail}
          reviewers={reviewers}
          canSubmit={canSubmit}
          principal={principal}
          isAdmin={isAdmin}
          onClose={() => { setSelectedProjectId(null); setSelectedDetail(null); }}
          onChanged={() => { load(); loadDetail(selectedProjectId); }}
          onOpenReview={(rid) => { setOpenReviewId(rid); setTab("review"); }}
        />
      )}

      {openReviewId && openReview && (
        <ReviewDetailModal
          review={openReview}
          principal={principal}
          isAdmin={isAdmin}
          onClose={() => setOpenReviewId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-4 py-2.5 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${active ? "text-[#d4af37] border-[#d4af37]" : "text-gray-500 border-transparent hover:text-gray-300"}`}>
      {label}
    </button>
  );
}

/* ------------------------- Onboarded tab -------------------------- */
function OnboardTab({ onboarded, onOpen }) {
  if (onboarded.length === 0) {
    return (
      <div className="iz-card p-12 text-center">
        <UserPlus className="w-8 h-8 mx-auto text-gray-600" strokeWidth={1.5}/>
        <p className="text-sm text-gray-400 mt-3">No clients onboarded yet.</p>
        <p className="text-[11px] text-gray-500 mt-1">Click "Onboard Client" to start.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3">
      {onboarded.map((p) => (
        <button key={p.id} onClick={() => onOpen(p)}
                data-testid={`onboard-row-${p.id}`}
                className="iz-card p-4 text-left hover:border-[#d4af37]/60 transition-colors group">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg text-white truncate">{p.client_name}</h3>
                <StatusPill status={p.review_status} testid={`onboard-status-${p.id}`}/>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{p.project_name}</p>
              <p className="text-[11px] text-gray-500 mt-0.5 font-mono">{p.client_email}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Initial Quote</p>
              <p className="font-display text-xl text-[#d4af37]" data-testid={`onboard-qv-${p.id}`}>
                ₹{inr(p.initial_quote_value)}
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">
                by {p.created_by_name} · {new Date(p.created_at).toLocaleDateString()}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#d4af37] self-center"/>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ------------------------- Review tab -------------------------- */
function ReviewTab({ reviews, principal, isAdmin, onOpen }) {
  const [filter, setFilter] = useState("all");
  const filtered = reviews.filter((r) => filter === "all" ? true : r.status === filter);

  if (reviews.length === 0) {
    return (
      <div className="iz-card p-12 text-center">
        <ClipboardList className="w-8 h-8 mx-auto text-gray-600" strokeWidth={1.5}/>
        <p className="text-sm text-gray-400 mt-3">No design reviews yet.</p>
        <p className="text-[11px] text-gray-500 mt-1">Submit an onboarded client for review to see it here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { k: "all", label: `All (${reviews.length})` },
          { k: "pending", label: `Pending (${reviews.filter((r) => r.status === "pending").length})` },
          { k: "approved", label: `Approved (${reviews.filter((r) => r.status === "approved").length})` },
          { k: "rejected", label: `Rejected (${reviews.filter((r) => r.status === "rejected").length})` },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
                  data-testid={`review-filter-${f.k}`}
                  className={`px-3 py-1 rounded text-[11px] font-mono uppercase tracking-widest border ${filter === f.k ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5" : "border-[#262626] text-gray-500"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3">
        {filtered.map((r) => {
          const isReviewer = principal?.id === r.reviewer_id;
          return (
            <button key={r.id} onClick={() => onOpen(r)}
                    data-testid={`review-row-${r.id}`}
                    className="iz-card p-4 text-left hover:border-[#d4af37]/60 transition-colors group">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg text-white truncate">{r.client_name}</h3>
                    <StatusPill status={r.status} testid={`review-status-${r.id}`}/>
                    {isReviewer && r.status === "pending" && (
                      <span className="text-[10px] font-mono uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5 px-2 py-0.5 rounded">
                        Action required
                      </span>
                    )}
                    {r.revision_count > 0 && (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                        R{r.revision_count}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{r.project_name}</p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Reviewer: <span className="text-gray-300">{r.reviewer_name}</span>
                    <span className="mx-1">·</span>
                    <span className="text-gray-500">{r.reviewer_role}</span>
                    <span className="mx-1">·</span>
                    Submitted by <span className="text-gray-300">{r.submitted_by_name}</span>
                    <span className="mx-1">·</span>
                    {new Date(r.submitted_at).toLocaleString()}
                  </p>
                  {r.comments && r.status !== "pending" && (
                    <p className="text-[11px] text-gray-400 mt-2 border-l-2 border-[#262626] pl-2 italic">
                      "{r.comments}"
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#d4af37] self-center"/>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------- Create onboard modal -------------------------- */
function CreateOnboardModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", project_name: "", initial_quote_value: "" });
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.client_email.trim() || !form.project_name.trim() || !form.initial_quote_value) {
      return toast.error("Please fill all fields");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ops/onboard", {
        client_name: form.client_name.trim(),
        client_email: form.client_email.trim().toLowerCase(),
        project_name: form.project_name.trim(),
        initial_quote_value: Number(form.initial_quote_value),
      });
      toast.success("Client onboarded");
      onCreated(data);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Onboarding failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
         data-testid="onboard-create-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Ops Onboard</p>
            <h3 className="font-display text-xl text-white mt-0.5">New Client</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="onboard-create-close">
            <X className="w-5 h-5" strokeWidth={1.5}/>
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <Field label="Client Name *">
            <input className="iz-input w-full" value={form.client_name}
                   onChange={(e) => setField("client_name", e.target.value)} required
                   data-testid="onboard-client-name"/>
          </Field>
          <Field label="Client Email *">
            <input type="email" className="iz-input w-full" value={form.client_email}
                   onChange={(e) => setField("client_email", e.target.value)} required
                   data-testid="onboard-client-email"/>
            <p className="text-[10px] text-gray-500 mt-1">Same email can be onboarded to multiple projects.</p>
          </Field>
          <Field label="Project Name *">
            <input className="iz-input w-full" value={form.project_name}
                   onChange={(e) => setField("project_name", e.target.value)} required
                   data-testid="onboard-project-name"/>
          </Field>
          <Field label="Initial Quote Value (₹) *">
            <input type="number" min="0" step="1" className="iz-input w-full" value={form.initial_quote_value}
                   onChange={(e) => setField("initial_quote_value", e.target.value)} required
                   data-testid="onboard-quote-value"/>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="iz-btn-secondary text-xs">Cancel</button>
            <button type="submit" disabled={busy} className="iz-btn-primary text-xs flex items-center gap-2"
                    data-testid="onboard-create-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <UserPlus className="w-3.5 h-3.5"/>}
              Onboard Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------- Project detail drawer -------------------------- */
function ProjectDetailDrawer({ detail, reviewers, canSubmit, principal, isAdmin, onClose, onChanged, onOpenReview }) {
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="onboard-detail-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose}/>
      <aside className="w-full max-w-2xl bg-[#080808] border-l border-[#262626] overflow-y-auto">
        <div className="sticky top-0 bg-[#080808] border-b border-[#262626] px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Ops Onboarded Client</p>
            <h2 className="font-display text-xl text-white mt-0.5">{detail.client_name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{detail.project_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="onboard-detail-close">
            <X className="w-5 h-5" strokeWidth={1.5}/>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-3">
            <Meta label="Email" value={detail.client_email}/>
            <Meta label="Initial Quote" value={`₹${inr(detail.initial_quote_value)}`}/>
            <Meta label="Onboarded By" value={detail.created_by_name || "—"}/>
            <Meta label="Onboarded On" value={new Date(detail.created_at).toLocaleDateString()}/>
          </div>

          {/* Current review status */}
          <div className="iz-card p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design Review</p>
              <StatusPill status={detail.review_status} testid="onboard-detail-status"/>
            </div>
            {detail.active_review ? (
              <ReviewSummary review={detail.active_review} onOpen={() => onOpenReview(detail.active_review.id)}/>
            ) : (
              <p className="text-xs text-gray-500">No design package submitted yet.</p>
            )}
          </div>

          {/* Submit / Resubmit */}
          {canSubmit && (detail.review_status === "not_submitted" || detail.review_status === "rejected") && (
            <SubmitDesignPanel
              projectId={detail.id}
              reviewers={reviewers.filter((r) => r.id !== principal?.id)}
              isRevision={detail.review_status === "rejected"}
              onDone={onChanged}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</p>
      <p className="text-sm text-white mt-0.5 truncate">{value}</p>
    </div>
  );
}

function ReviewSummary({ review, onOpen }) {
  return (
    <div className="text-xs text-gray-400 space-y-1">
      <p>Reviewer: <span className="text-gray-200">{review.reviewer_name}</span> <span className="text-gray-500">· {review.reviewer_role}</span></p>
      <p>Submitted: <span className="text-gray-200">{new Date(review.submitted_at).toLocaleString()}</span></p>
      {review.comments && (
        <p className="text-gray-400 border-l-2 border-[#262626] pl-2 italic mt-2">"{review.comments}"</p>
      )}
      <button onClick={onOpen} className="iz-btn-secondary text-xs mt-3 flex items-center gap-1.5"
              data-testid="onboard-detail-view-review">
        <FileText className="w-3 h-3"/> View Files & History
      </button>
    </div>
  );
}

/* ------------------------- Submit design panel -------------------------- */
function SubmitDesignPanel({ projectId, reviewers, isRevision, onDone }) {
  const [files, setFiles] = useState({ two_d: null, three_d: null, ppt: null, quotation: null });
  const [reviewerId, setReviewerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null); // slot key while uploading

  const setSlot = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const rec = await uploadOne(file);
      setFiles((f) => ({ ...f, [slot]: rec }));
      toast.success(`${SLOT_LABELS[slot]} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(null); }
  };

  const submit = async () => {
    if (!files.two_d || !files.three_d || !files.quotation) {
      return toast.error("2D, 3D and Quotation are required");
    }
    if (!reviewerId) return toast.error("Please pick a reviewer");
    setBusy(true);
    try {
      await api.post(`/ops/onboard/${projectId}/design-review`, {
        two_d_file_id: files.two_d.id,
        three_d_file_id: files.three_d.id,
        ppt_file_id: files.ppt?.id || null,
        quotation_file_id: files.quotation.id,
        reviewer_id: reviewerId,
      });
      toast.success(isRevision ? "Resubmitted for review" : "Submitted for review");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="iz-card p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          {isRevision ? "Resubmit For Review" : "Submit For Review"}
        </p>
      </div>
      <div className="space-y-3">
        {SLOT_ORDER.map((slot) => (
          <FileSlot
            key={slot}
            label={SLOT_LABELS[slot]}
            required={slot !== "ppt"}
            icon={SLOT_ICONS[slot]}
            file={files[slot]}
            uploading={uploading === slot}
            onPick={(f) => setSlot(slot, f)}
            testid={`upload-slot-${slot}`}
          />
        ))}
        <Field label="Reviewer *">
          <select className="iz-input w-full" value={reviewerId}
                  onChange={(e) => setReviewerId(e.target.value)}
                  data-testid="submit-reviewer-select">
            <option value="">Select reviewer…</option>
            {reviewers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} · {u.role_name}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-gray-500 mt-1">Options: Director, Operations Manager, Operations Executive.</p>
        </Field>
        <button onClick={submit} disabled={busy || !files.two_d || !files.three_d || !files.quotation || !reviewerId}
                className="iz-btn-primary w-full text-xs flex items-center justify-center gap-2 mt-3"
                data-testid="submit-review-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
          {isRevision ? "Resubmit" : "Submit for Review"}
        </button>
      </div>
    </div>
  );
}

const SLOT_ORDER = ["two_d", "three_d", "ppt", "quotation"];
const SLOT_LABELS = {
  two_d: "2D Drawings",
  three_d: "3D Renders",
  ppt: "PPT / Deck",
  quotation: "Draft Quotation",
};
const SLOT_ICONS = {
  two_d: ImageIcon,
  three_d: Layers3,
  ppt: Presentation,
  quotation: FileText,
};

function FileSlot({ label, required, icon: Icon, file, uploading, onPick, testid }) {
  const inputRef = React.useRef(null);
  return (
    <div className="border border-[#262626] rounded p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
          <p className="text-xs text-gray-300">{label} {required && <span className="text-red-400">*</span>}</p>
        </div>
        <input ref={inputRef} type="file" hidden onChange={(e) => onPick(e.target.files?.[0])}
               data-testid={`${testid}-input`}/>
        <div className="flex items-center gap-2">
          {file && (
            <a href={fileUrl(file.id)} target="_blank" rel="noreferrer"
               className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] hover:underline">
              Open
            </a>
          )}
          <button type="button" onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="iz-btn-secondary text-[10px] font-mono uppercase tracking-widest flex items-center gap-1"
                  data-testid={`${testid}-btn`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin"/> : <ChevronRight className="w-3 h-3"/>}
            {file ? "Replace" : "Upload"}
          </button>
        </div>
      </div>
      {file && (
        <p className="text-[10px] text-gray-500 mt-1 truncate font-mono">{file.original_filename}</p>
      )}
    </div>
  );
}

/* ------------------------- Review detail modal -------------------------- */
function ReviewDetailModal({ review, principal, isAdmin, onClose, onChanged }) {
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(null); // "approve" | "reject"
  const isReviewer = isAdmin || principal?.id === review.reviewer_id;
  const canDecide = isReviewer && review.status === "pending";

  const decide = async (action) => {
    if (action === "reject" && !comments.trim()) {
      return toast.error("Please add rejection comments");
    }
    setBusy(action);
    try {
      await api.post(`/ops/design-reviews/${review.id}/${action}`, {
        comments: comments.trim() || null,
      });
      toast.success(action === "approve" ? "Design approved" : "Design rejected");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(null); }
  };

  const files = review.files || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
         data-testid="review-detail-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design Review</p>
            <h3 className="font-display text-xl text-white mt-0.5">{review.client_name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{review.project_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={review.status}/>
            <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="review-detail-close">
              <X className="w-5 h-5" strokeWidth={1.5}/>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Meta label="Reviewer" value={`${review.reviewer_name} · ${review.reviewer_role}`}/>
            <Meta label="Submitted By" value={review.submitted_by_name}/>
            <Meta label="Submitted At" value={new Date(review.submitted_at).toLocaleString()}/>
            <Meta label="Initial Quote" value={`₹${inr(review.initial_quote_value)}`}/>
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Files</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SLOT_ORDER.map((slot) => {
                const fid = files[slot];
                const Icon = SLOT_ICONS[slot];
                return (
                  <div key={slot} className={`border rounded p-3 flex items-center justify-between ${fid ? "border-[#d4af37]/40 bg-[#d4af37]/5" : "border-[#262626]"}`}
                       data-testid={`review-file-${slot}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
                      <p className="text-xs text-gray-300">{SLOT_LABELS[slot]}</p>
                    </div>
                    {fid ? (
                      <a href={fileUrl(fid)} target="_blank" rel="noreferrer"
                         className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] hover:underline"
                         data-testid={`review-file-open-${slot}`}>
                        Open
                      </a>
                    ) : (
                      <span className="text-[10px] text-gray-600 italic">Not provided</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Decision panel */}
          {canDecide && (
            <div className="iz-card p-4 space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Review Decision</p>
              <textarea rows={3} className="iz-input w-full" placeholder="Comments (required for rejection)"
                        value={comments} onChange={(e) => setComments(e.target.value)}
                        data-testid="review-comments"/>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => decide("reject")} disabled={busy}
                        className="iz-btn-secondary text-xs flex items-center gap-1.5 hover:border-red-500 hover:text-red-400"
                        data-testid="review-reject-btn">
                  {busy === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <MessageSquare className="w-3.5 h-3.5"/>}
                  Reject & Send Back
                </button>
                <button onClick={() => decide("approve")} disabled={busy}
                        className="iz-btn-primary text-xs flex items-center gap-1.5"
                        data-testid="review-approve-btn">
                  {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Check className="w-3.5 h-3.5"/>}
                  Approve
                </button>
              </div>
            </div>
          )}

          {review.comments && review.status !== "pending" && (
            <div className="border-l-2 border-[#d4af37] pl-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
                {review.status === "approved" ? "Approval Note" : "Rejection Reason"}
              </p>
              <p className="text-sm text-gray-300 mt-1">{review.comments}</p>
              <p className="text-[10px] text-gray-500 mt-1 font-mono">
                by {review.decided_by_name} · {new Date(review.decided_at).toLocaleString()}
              </p>
            </div>
          )}

          {Array.isArray(review.iterations) && review.iterations.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">
                Previous Iterations ({review.iterations.length})
              </p>
              <div className="space-y-3">
                {review.iterations.slice().reverse().map((it, idx) => (
                  <IterationCard key={it.revision ?? `iter-${idx}`} iteration={it}/>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(review.history) && review.history.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">History</p>
              <ol className="space-y-2">
                {review.history.map((h, idx) => (
                  <li key={h.at ? `${h.at}-${h.action}` : `h-${idx}`} className="text-[11px] text-gray-400 border-l border-[#262626] pl-3">
                    <span className="text-[#d4af37] font-mono uppercase">{h.action}</span>
                    <span className="mx-1 text-gray-600">·</span>
                    <span className="text-gray-300">{h.by_name}</span>
                    <span className="mx-1 text-gray-600">·</span>
                    <span className="text-gray-500 font-mono">{new Date(h.at).toLocaleString()}</span>
                    {h.comments && <p className="text-gray-400 italic mt-0.5">"{h.comments}"</p>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IterationCard({ iteration }) {
  const [open, setOpen] = useState(false);
  const statusMeta = STATUS_META[iteration.status] || STATUS_META.pending;
  const files = iteration.files || {};
  const providedSlots = SLOT_ORDER.filter((s) => files[s]);
  return (
    <div className="border border-[#262626] rounded" data-testid={`iteration-r${iteration.revision}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#111] transition-colors"
              data-testid={`iteration-toggle-r${iteration.revision}`}>
        <div className="flex items-center gap-2 flex-wrap text-left">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
            Iteration R{iteration.revision}
          </span>
          <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 rounded ${statusMeta.tone}`}>
            {statusMeta.label}
          </span>
          <span className="text-[11px] text-gray-500">
            {iteration.submitted_by_name} · {new Date(iteration.submitted_at).toLocaleDateString()}
          </span>
        </div>
        <ChevronRight className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? "rotate-90" : ""}`}/>
      </button>
      {open && (
        <div className="border-t border-[#262626] p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {providedSlots.map((slot) => {
              const Icon = SLOT_ICONS[slot];
              return (
                <div key={slot} className="border border-[#262626] rounded p-2 flex items-center justify-between"
                     data-testid={`iteration-r${iteration.revision}-file-${slot}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-3.5 h-3.5 text-[#d4af37]" strokeWidth={1.5}/>
                    <p className="text-[11px] text-gray-300">{SLOT_LABELS[slot]}</p>
                  </div>
                  <a href={fileUrl(files[slot])} target="_blank" rel="noreferrer"
                     className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] hover:underline">
                    Open
                  </a>
                </div>
              );
            })}
            {providedSlots.length === 0 && (
              <p className="text-[11px] text-gray-500 italic col-span-full">No files archived for this iteration.</p>
            )}
          </div>
          <div className="text-[11px] text-gray-400">
            Reviewer: <span className="text-gray-300">{iteration.reviewer_name}</span>
            {iteration.reviewer_role && <span className="text-gray-500"> · {iteration.reviewer_role}</span>}
          </div>
          {iteration.comments && (
            <div className="border-l-2 border-[#d4af37] pl-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
                {iteration.status === "approved" ? "Approval Note" : "Rejection Reason"}
              </p>
              <p className="text-xs text-gray-300 mt-0.5">{iteration.comments}</p>
              {iteration.decided_by_name && (
                <p className="text-[10px] text-gray-500 font-mono mt-0.5">
                  by {iteration.decided_by_name} · {iteration.decided_at ? new Date(iteration.decided_at).toLocaleString() : ""}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
